import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@microservices/wiskey-react-components'
import {
  DataGrid,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
} from '@microservices/wiskey-react-table-component'
import { ContentCopy, Delete, Edit } from '@mui/icons-material'
import { Grid } from '@mui/material'

import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'

import { useEditConfig } from '@providers/EditConfigProvider'

import { generateConfigSelectColumn } from '@helpers'
import {
  CONFIG_MENU_OPTION,
  CONFIG_TOOLTIPS_COLUMNS,
  DEFAULT_HEADER_STYLES_CLASSNAME,
  FLEXIBLE,
  PINNED_COLUMN,
} from '@constants'

type AllTooltipsTableProps = {
  rows: any[]
  loading: boolean
  onDelete: (id: number) => void
  onCopy: (data: any) => void
  onEdit: (id: number) => void
}

export const AllTooltipsTable: FC<AllTooltipsTableProps> = ({
  rows,
  loading,
  onDelete,
  onCopy,
  onEdit,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [currentRowId, setCurrentRowId] = useState<number>()
  const { t } = useTranslation()

  const {
    configMenuOption,
    selectedConfigId,
    handleSelectConfigId,
    isExportAllSelected,
    handleSelectExportAll,
    configFileText,
    handleApply,
    handleFileUpload,
    isOpenScriptDialog,
    handleCancel,
    handleCopyScript,
  } = useEditConfig()

  const handleCopy = (params: GridCellParams) => {
    const { id, title } = params.row
    onCopy?.({ id, title })
  }

  const handleEdit = (params: GridCellParams) => {
    onEdit?.(params.row.id)
  }

  const handleClickDelete = (params: GridCellParams) => {
    if (params.row.id) {
      setShowDeleteModal(true)
      setCurrentRowId(params.row.id)
    }
  }

  const handleDelete = () => {
    if (currentRowId) {
      onDelete?.(currentRowId)
      setShowDeleteModal(false)
    }
  }

  const handleClose = () => setShowDeleteModal(false)

  const columnsWithActions = [
    ...CONFIG_TOOLTIPS_COLUMNS,
    configMenuOption &&
      configMenuOption !== CONFIG_MENU_OPTION.IMPORT &&
      generateConfigSelectColumn({
        selectedConfigId,
        onSelectRow: handleSelectConfigId,
      }),
    {
      field: 'actions',
      type: 'actions',
      align: 'center',
      minWidth: 120,
      resizable: false,
      hideable: false,
      headerClassName: `${DEFAULT_HEADER_STYLES_CLASSNAME}`,
      pinnedColumn: PINNED_COLUMN.RIGHT,
      getActions: (params: GridCellParams) => [
        <GridActionsCellItem
          key={params.id}
          icon={<ContentCopy />}
          label='Copy'
          showInMenu={false}
          onClick={() => handleCopy(params)}
        />,
        <GridActionsCellItem
          key={params.id}
          icon={<Edit />}
          label='Edit'
          showInMenu={false}
          onClick={() => handleEdit(params)}
        />,
        <GridActionsCellItem
          key={params.id}
          icon={<Delete />}
          label='Delete'
          showInMenu={false}
          onClick={() => handleClickDelete(params)}
        />,
      ],
    } as GridColDef,
  ]

  return (
    <Grid container p={1} sx={FLEXIBLE}>
      <EditConfigScriptDialog />
      {showDeleteModal && (
        <ConfirmModal
          actionBtnText={t('modal.delete.btn')}
          isShow={showDeleteModal}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
            // borderRadius: 0
          }}
          onClose={handleClose}
          onConfirm={handleDelete}
        />
      )}
      <Grid container item sx={FLEXIBLE}>
        <DataGrid
          hideFooter
          columns={columnsWithActions}
          loading={loading}
          rows={rows}
          sx={{ borderRadius: 0 }}
        />
      </Grid>
      <ExportActionsPanel />
    </Grid>
  )
}
